import {
  createContext,
  useState,
  useContext,
  useCallback,
  useMemo,
} from 'react'

import type { INPS, IPathSelected } from '../../types'
import type { NPSContextType } from './types'
import { FunctionComponentChildren } from '@/@types/react'

const NPSContext = createContext<NPSContextType>(null)

export const NPSProvider: FunctionComponentChildren = ({ children }) => {
  const [NPSAnswers, setNPSAnswers] = useState<INPS>({})
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [selectedPath, setSelectedPath] = useState<IPathSelected>(null)

  const updateNPSAnswers = useCallback((updatedNPSAnswers: INPS) => {
    setNPSAnswers(updatedNPSAnswers)
  }, [])

  const updateNPSAnswersPartially = useCallback(
    (updatedNPSAnswers: Partial<INPS>) => {
      setNPSAnswers(prevState => {
        return {
          ...prevState,
          ...updatedNPSAnswers,
        }
      })
    },
    [],
  )

  const nextStep = useCallback(() => {
    setCurrentStep(currentStep + 1)
  }, [currentStep])

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }, [currentStep])

  const choosePath = useCallback(
    (path: IPathSelected) => {
      setSelectedPath(path)
      nextStep()
    },
    [nextStep],
  )

  const values = useMemo(
    () => ({
      NPSAnswers,
      updateNPSAnswers,
      updateNPSAnswersPartially,
      currentStep,
      selectedPath,
      choosePath,
      nextStep,
      prevStep,
    }),
    [
      NPSAnswers,
      updateNPSAnswers,
      updateNPSAnswersPartially,
      currentStep,
      selectedPath,
      choosePath,
      nextStep,
      prevStep,
    ],
  )

  return <NPSContext.Provider value={values}>{children}</NPSContext.Provider>
}

export const useNPSContext = (): NPSContextType => {
  const context = useContext(NPSContext)

  if (!context) {
    throw new Error('useNPSContext must be used within a NPSProvider')
  }

  return context
}
